/**
 * Shared MarketMap visualization utilities
 *
 * Extracted from MarketMap component for reuse in:
 * - MarketMap/index.tsx (Ideas page)
 * - ChatPage/Tools/MarketMapRenderer.tsx (Chat tool output)
 */

import { FC } from 'react';
import { css } from '@emotion/react';
import { colors, spacing, fontStyles } from '../../styles';

// Color gradient for performance - tungsten (neutral) at 0%, leaf/berry at extremes
// Uses logarithmic scaling so 1% vs 10% vs 100% all show distinct colors
export const getPerformanceColor = (changePercent: number): string => {
  const absChange = Math.abs(changePercent);
  // Logarithmic scaling: log(1 + x) / log(1 + max)
  // This gives good differentiation across 1%, 10%, 50%, 100%+ ranges
  const intensity = Math.min(1, Math.log(1 + absChange) / Math.log(1 + 100));

  // colors.tungsten as neutral, colors.leaf (green) and colors.berry (red) at extremes
  const neutral = { r: 80, g: 78, b: 75 }; // #504E4B tungsten
  const leaf = { r: 24, g: 157, b: 57 }; // #189D39 leaf
  const berry = { r: 208, g: 63, b: 23 }; // #D03F17 berry

  const target = changePercent >= 0 ? leaf : berry;
  const r = Math.round(neutral.r + (target.r - neutral.r) * intensity);
  const g = Math.round(neutral.g + (target.g - neutral.g) * intensity);
  const b = Math.round(neutral.b + (target.b - neutral.b) * intensity);
  return `rgb(${r}, ${g}, ${b})`;
};

// Custom treemap cell renderer props
export interface CustomContentProps {
  x: number;
  y: number;
  width: number;
  height: number;
  name: string;
  change_percent: number;
  symbol: string;
  display_name?: string;
}

// Custom treemap cell renderer
export const CustomizedContent: FC<CustomContentProps> = (props) => {
  const { x, y, width, height, change_percent, symbol, display_name } = props;

  if (!symbol) return null;

  // Handle null/undefined change_percent
  const safeChangePercent = change_percent ?? 0;

  // Use display_name if available (for ETF views), otherwise use symbol
  const label = display_name || symbol;

  // Split label into lines if it contains a space (e.g., "Russell 2000" -> ["Russell", "2000"])
  const labelLines = label.includes(' ') ? label.split(' ') : [label];
  const isMultiLine = labelLines.length > 1;

  const color = getPerformanceColor(safeChangePercent);
  const isPositive = safeChangePercent >= 0;
  const showText = width > 40 && height > 30;
  const showPercent = width > 55 && height > 45;

  // Calculate font size
  const fontSize = Math.min(14, Math.max(10, width / 5));
  const lineHeight = fontSize * 1.2;

  // Calculate vertical positioning for label
  // For multi-line: shift up to make room for both lines
  // For single line: center or shift up if showing percent
  const labelBaseY = isMultiLine
    ? y + height / 2 - lineHeight / 2 - (showPercent ? 6 : 0)
    : y + height / 2 - (showPercent ? 8 : 0);

  return (
    <g>
      <rect
        x={x}
        y={y}
        width={width}
        height={height}
        style={{
          fill: color,
          stroke: '#fff',
          strokeWidth: 2,
          cursor: 'pointer',
        }}
        rx={4}
        ry={4}
      />
      {/* IMPORTANT: Use SVG attributes (fill, stroke) instead of CSS style prop.
          Global CSS in styles/index.tsx sets `svg { fill: var(--text-color) }` which
          overrides CSS fill. SVG attributes take precedence over CSS, preventing the
          ugly outline effect. Do not use textShadow - it doesn't work on SVG text. */}
      {showText && (
        <text
          x={x + width / 2}
          y={labelBaseY}
          textAnchor="middle"
          dominantBaseline="middle"
          fontSize={fontSize}
          fontWeight={500}
          fontFamily="HelveticaNowText, sans-serif"
          fill="#ffffff"
          stroke="none"
        >
          {labelLines.map((line, index) => (
            <tspan key={index} x={x + width / 2} dy={index === 0 ? 0 : lineHeight}>
              {line}
            </tspan>
          ))}
        </text>
      )}
      {showPercent && (
        <text
          x={x + width / 2}
          y={y + height / 2 + (isMultiLine ? 18 : 12)}
          textAnchor="middle"
          dominantBaseline="middle"
          fontSize={Math.min(12, Math.max(9, width / 6))}
          fontWeight={400}
          fontFamily="HelveticaNowText, sans-serif"
          fill="rgba(255,255,255,0.85)"
          stroke="none"
        >
          {isPositive ? '+' : ''}
          {safeChangePercent.toFixed(1)}%
        </text>
      )}
    </g>
  );
};

// Custom treemap cell renderer - blurred version for locked premium views
export const CustomizedContentBlurred: FC<CustomContentProps> = (props) => {
  const { x, y, width, height, change_percent, symbol, display_name } = props;

  if (!symbol) return null;

  // Handle null/undefined change_percent
  const safeChangePercent = change_percent ?? 0;

  // Use display_name if available (for ETF views), otherwise use symbol
  const label = display_name || symbol;

  // Split label into lines if it contains a space
  const labelLines = label.includes(' ') ? label.split(' ') : [label];
  const isMultiLine = labelLines.length > 1;

  const color = getPerformanceColor(safeChangePercent);
  const isPositive = safeChangePercent >= 0;
  const showText = width > 40 && height > 30;
  const showPercent = width > 55 && height > 45;

  // Calculate font size
  const fontSize = Math.min(14, Math.max(10, width / 5));
  const lineHeight = fontSize * 1.2;

  // Calculate vertical positioning for label
  const labelBaseY = isMultiLine
    ? y + height / 2 - lineHeight / 2 - (showPercent ? 6 : 0)
    : y + height / 2 - (showPercent ? 8 : 0);

  // Generate placeholder text for blurred symbols (same length, obscured)
  const blurredLabel = label.replace(/[A-Za-z0-9]/g, '•');
  const blurredLines = blurredLabel.includes(' ') ? blurredLabel.split(' ') : [blurredLabel];

  return (
    <g>
      <rect
        x={x}
        y={y}
        width={width}
        height={height}
        style={{
          fill: color,
          stroke: '#fff',
          strokeWidth: 2,
          cursor: 'default',
        }}
        rx={4}
        ry={4}
      />
      {showText && (
        <text
          x={x + width / 2}
          y={labelBaseY}
          textAnchor="middle"
          dominantBaseline="middle"
          fontSize={fontSize}
          fontWeight={500}
          fontFamily="HelveticaNowText, sans-serif"
          fill="rgba(255,255,255,0.5)"
          stroke="none"
          style={{ filter: 'blur(4px)' }}
        >
          {blurredLines.map((line, index) => (
            <tspan key={index} x={x + width / 2} dy={index === 0 ? 0 : lineHeight}>
              {line}
            </tspan>
          ))}
        </text>
      )}
      {showPercent && (
        <text
          x={x + width / 2}
          y={y + height / 2 + (isMultiLine ? 18 : 12)}
          textAnchor="middle"
          dominantBaseline="middle"
          fontSize={Math.min(12, Math.max(9, width / 6))}
          fontWeight={400}
          fontFamily="HelveticaNowText, sans-serif"
          fill="rgba(255,255,255,0.5)"
          stroke="none"
          style={{ filter: 'blur(4px)' }}
        >
          {isPositive ? '+' : ''}
          {safeChangePercent.toFixed(1)}%
        </text>
      )}
    </g>
  );
};

// Custom tooltip payload type
export interface TooltipPayload {
  payload: {
    name: string;
    symbol: string;
    change_percent: number;
    market_cap: number;
  };
}

// Format market cap for display
export const formatMarketCap = (value: number): string => {
  if (value >= 1e12) return `$${(value / 1e12).toFixed(2)}T`;
  if (value >= 1e9) return `$${(value / 1e9).toFixed(2)}B`;
  if (value >= 1e6) return `$${(value / 1e6).toFixed(2)}M`;
  return `$${value.toLocaleString()}`;
};

// Custom tooltip component
export const CustomTooltip: FC<{ active?: boolean; payload?: TooltipPayload[] }> = ({ active, payload }) => {
  if (!active || !payload || payload.length === 0) return null;

  const data = payload[0].payload;
  const safeChangePercent = data.change_percent ?? 0;

  return (
    <div
      css={css`
        background: ${colors.snow};
        border: 1px solid rgba(203, 202, 201, 0.5);
        border-radius: 8px;
        padding: ${spacing.deci};
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);

        html[data-theme='dark'] & {
          background: #2a2a2a;
          border-color: rgba(80, 78, 75, 0.5);
        }
      `}
    >
      <div
        css={css`
          ${fontStyles.dog}
          font-weight: 600;
          color: ${colors.petrolBlue};
          margin-bottom: 4px;

          html[data-theme='dark'] & {
            color: ${colors.sugar};
          }
        `}
      >
        {data.symbol}
      </div>
      <div
        css={css`
          ${fontStyles.cat}
          color: ${safeChangePercent >= 0 ? colors.leaf : colors.berry};
          font-weight: 600;
        `}
      >
        {safeChangePercent > 0 ? '+' : ''}
        {safeChangePercent.toFixed(2)}%
      </div>
      {data.market_cap && (
        <div
          css={css`
            ${fontStyles.cat}
            color: ${colors.silver};
            margin-top: 4px;

            html[data-theme='dark'] & {
              color: ${colors.tungsten};
            }
          `}
        >
          {formatMarketCap(data.market_cap)}
        </div>
      )}
    </div>
  );
};
